import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { PrivilegeType } from '@/consts/team'

interface UsePlatformBalanceResult {
  platformBalance: number // in cents
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  getPlatformToken: () => Promise<string | null>
}

/**
 * Hook to fetch platform account balance when in team context.
 * Uses privilege_type: 58 to get a platform token, then fetches /user/account.
 */
export function usePlatformBalance(enabled: boolean = true): UsePlatformBalanceResult {
  const [platformBalance, setPlatformBalance] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const getPlatformToken = useCallback(async (): Promise<string | null> => {
    try {
      const res = await API.getPlatformToken({
        privilege_type: PrivilegeType.PlatformToken,
      })
      if (res.token) {
        return `Bearer ${res.token}`
      }
      return null
    } catch (err) {
      console.error('Failed to get platform token:', err)
      return null
    }
  }, [])

  const fetchPlatformBalance = useCallback(async () => {
    if (!enabled) return

    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Get platform token
      const platformToken = await getPlatformToken()
      if (!platformToken) {
        setError('Failed to get platform token')
        return
      }

      // Step 2: Fetch account with platform token
      const res = await API.getAccount(undefined, {
        headers: {
          Authorization: platformToken,
        },
      })

      if (res.code === 0 && res.account) {
        setPlatformBalance(res.account.totalBonus?.moneyCent ?? 0)
      } else {
        setError(res.msg || 'Failed to fetch platform balance')
      }
    } catch (err) {
      console.error('Failed to fetch platform balance:', err)
      setError('Failed to fetch platform balance')
    } finally {
      setIsLoading(false)
    }
  }, [enabled, getPlatformToken])

  useEffect(() => {
    if (enabled) {
      fetchPlatformBalance()
    }
  }, [enabled, fetchPlatformBalance])

  return {
    platformBalance,
    isLoading,
    error,
    refetch: fetchPlatformBalance,
    getPlatformToken,
  }
}
